// utils/applyOffers.js
import { parseOffer } from "./offerParser";

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function expandUnits(items) {
  const units = [];
  items.forEach((item) => {
    const count = toNum(item.count) || 1;
    const price = toNum(item.price);
    for (let i = 0; i < count; i++) {
      units.push({ ...item, unitPrice: price });
    }
  });
  return units;
}

// BOGO family: within every (buy+free) block, the cheapest `free` units are free
function priceBogoGroup(units, rule) {
  const blockSize = rule.buy + rule.free;
  const sorted = [...units].sort((a, b) => b.unitPrice - a.unitPrice);

  return sorted.map((unit, idx) => {
    const posInBlock = idx % blockSize;
    const isFree = posInBlock >= rule.buy; // last `free` slots in each block
    return { ...unit, isFree, payable: isFree ? 0 : unit.unitPrice };
  });
}

// Percent off: every unit gets the discount
function pricePercentGroup(units, rule) {
  return units.map((unit) => {
    const payable = unit.unitPrice * (1 - rule.value / 100);
    return { ...unit, isFree: false, payable: Math.round(payable * 100) / 100 };
  });
}

export function applyBOGO(cartItems) {
  // Bucket items by their normalized offer string
  const groups = {}; // key: raw offer string -> items[]
  const noOffer = [];

  cartItems.forEach((item) => {
    const rule = parseOffer(item.offer);
    if (!rule) {
      noOffer.push(item);
      return;
    }
    const key = item.offer.trim();
    if (!groups[key]) groups[key] = { rule, items: [] };
    groups[key].items.push(item);
  });

  let pricedUnits = [];

  // Price each offer group with its matching logic
  Object.values(groups).forEach(({ rule, items }) => {
    const units = expandUnits(items);
    if (rule.type === "bogo") {
      pricedUnits = pricedUnits.concat(priceBogoGroup(units, rule));
    } else if (rule.type === "percent") {
      pricedUnits = pricedUnits.concat(pricePercentGroup(units, rule));
    } else {
      pricedUnits = pricedUnits.concat(
        units.map((u) => ({ ...u, isFree: false, payable: u.unitPrice }))
      );
    }
  });

  // Items with no offer at all: full price
  pricedUnits = pricedUnits.concat(
    expandUnits(noOffer).map((u) => ({ ...u, isFree: false, payable: u.unitPrice }))
  );

  const totalOriginal = pricedUnits.reduce((sum, u) => sum + toNum(u.unitPrice), 0);
  const totalPayable = pricedUnits.reduce((sum, u) => sum + toNum(u.payable), 0);
  const totalDiscount = totalOriginal - totalPayable;

  return { pricedUnits, totalOriginal, totalPayable, totalDiscount };
}

export function groupPricedByProduct(pricedUnits) {
  const map = {};
  pricedUnits.forEach((u) => {
    if (!map[u.id]) {
      map[u.id] = { ...u, count: 0, freeCount: 0, payableAmount: 0 };
    }
    map[u.id].count += 1;
    map[u.id].freeCount += u.isFree ? 1 : 0;
    map[u.id].payableAmount += toNum(u.payable);
  });
  return Object.values(map);
}